import { Ban, CheckCircle2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { PermissionPicker } from '../../../../components/PermissionPicker';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { usePermissionCatalog } from '../usePermissionCatalog';
import { useRoleProfile } from '../useRoleProfile';

type TabKey = 'info' | 'permissions';

/**
 * The role profile — what a role is, and exactly what it grants.
 *
 * On the **header** layout rather than the sidebar one: a role reads as a single continuous
 * thing — a name, a handful of facts, and one long list of permissions — rather than a record
 * whose content splits into panels a reader chooses between.
 *
 * It exists because a role's permissions had no page. They were reachable only through the
 * edit modal on the roles list, so "what does Staff actually grant" could not be linked to
 * from the account that holds it.
 */
export function RoleProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { role, loading, error, reload } = useRoleProfile(id);
  const { catalog, loading: catalogLoading } = usePermissionCatalog();

  const [tab, setTab] = useState<TabKey>('info');

  // A plain string is a segment that goes nowhere; only Roles is a link back.
  const breadcrumb: BreadcrumbSegment[] = [
    'Administration',
    { label: 'Roles', to: '/roles' },
    role?.name ?? 'Role',
  ];

  if (error) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={reload}
        />
      </div>
    );
  }

  if (loading || !role) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="role-profile"
        header={
          <ProfileHeader
            id="role-profile-header"
            icon={<ShieldCheck className="h-6 w-6" aria-hidden="true" />}
            title={role.name}
            meta={[
              role.description,
              /*
                Counts of what the role reaches, unlabelled — the header's meta line is for
                facts that need no label, and "3 members" says what it is on its own.
              */
              `${role.userCount} ${role.userCount === 1 ? 'member' : 'members'}`,
              role.isSystem ? 'System role' : '',
            ]}
            status={
              <StatusText tone={role.isActive ? 'active' : 'inactive'}>
                {role.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={role.createdByName}
            createdAt={role.createdAt}
          />
        }
        banner={
          role.isActive ? (
            <ProfileBanner
              id="role-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={
                role.isSystem
                  ? 'This is a system role. It is granted every permission on boot and cannot be deleted.'
                  : 'This role is active. Accounts assigned to it hold the permissions below.'
              }
            />
          ) : (
            <ProfileBanner
              id="role-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This role is deleted. It grants nothing until it is restored."
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'permissions',
            label: (
              <>
                Permissions
                <CountBadge count={role.permissionKeys.length} tone="slate" />
              </>
            ),
            /*
              The panel renders the permission catalog, which is the same endpoint the role
              editor reads. A caller without the key would open the tab onto an error state.
            */
            permission: 'roles.read',
          },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard id="role-profile-basic-info" title="Role">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Name', value: role.name },
                  { label: 'Description', value: role.description },
                  { label: 'Members', value: String(role.userCount) },
                  { label: 'Permissions', value: String(role.permissionKeys.length) },
                ]}
              />
            </ProfileCard>

            {/* Audit is never editable — no cog, at any permission level. */}
            <ProfileCard id="role-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  {
                    label: 'Created by',
                    value: role.createdByName,
                  },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(role.createdAt)} className="font-mono">
                        {formatDateTime(role.createdAt)}
                      </time>
                    ),
                  },
                  {
                    label: 'Updated by',
                    value: role.updatedByName,
                  },
                  {
                    label: 'Latest update',
                    value: role.updatedAt ? (
                      <time dateTime={toIso(role.updatedAt)} className="font-mono">
                        {formatDateTime(role.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : null}

        {tab === 'permissions' ? (
          <ProfileCard
            padded
            id="role-profile-permissions"
            title="Granted permissions"
            count={role.permissionKeys.length}
            description="What every account assigned this role can do, unless its own grants were trimmed when it was created."
          >
            {catalogLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              /*
                Read-only, and opened on the modules this role actually grants — the same
                treatment the user profile gives an account's effective set. Changing a role's
                permissions is the roles list's own editor; this page states what they are.
              */
              <PermissionPicker
                idPrefix="role-profile-perm"
                catalog={catalog}
                value={role.permissionKeys}
                disabled
                showOnlyEnabled
                onChange={() => undefined}
                emptyMessage="No permissions are registered yet."
              />
            )}
          </ProfileCard>
        ) : null}
      </ProfileHeaderShell>
    </div>
  );
}
